import type {CertificateLines} from './layouts'

const SEPARATOR = ' · '

const DISCLAIMER =
  'Confirms completion of an Ad Astra Skills Academy program; not a professional certification or license.'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export type CertificateTextInputs = {
  learnerName: string
  courseName: string
  languagePair: string
  duration: string
  passMark?: number | null
  completionDate: string
  certificateNumber: string
  verifyUrl: string
}

function joinParts(...parts: string[]): string {
  return parts.filter((part) => part.trim()).join(SEPARATOR)
}

export function formatCertificateHours(hours: number | null | undefined): string {
  if (!hours || hours <= 0) {
    return ''
  }
  const text = String(Math.round(hours * 10) / 10)
  return text === '1' ? '1 hour' : `${text} hours`
}

export function formatCertificateDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) {
    return ''
  }
  if (minutes <= 60) {
    return minutes === 1 ? '1 minute' : `${minutes} minutes`
  }
  return formatCertificateHours(Math.floor((minutes * 10 + 30) / 60) / 10)
}

export function formatCertificateLanguagePair(
  fromName: string | null | undefined,
  toName: string | null | undefined
): string {
  return fromName?.trim() && toName?.trim() ? `${fromName.trim()} and ${toName.trim()}` : ''
}

export function formatCertificateDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, '0')} ${
    MONTHS[date.getMonth()]
  } ${date.getFullYear()}`
}

export function composeCertificateLines(inputs: CertificateTextInputs): CertificateLines {
  const credential = `Credential No. ${inputs.certificateNumber}`
  const verify = `Verify at ${inputs.verifyUrl}`
  return {
    learnerName: inputs.learnerName,
    courseName: inputs.courseName,
    completionDate: inputs.completionDate,
    detailsLetter: joinParts(
      inputs.languagePair,
      inputs.duration,
      inputs.passMark != null ? `Passed at ${inputs.passMark}% or higher` : ''
    ),
    detailsSocial: joinParts(inputs.languagePair, `Completed ${inputs.completionDate}`),
    credential,
    verifyLetter: `${verify}. ${DISCLAIMER}`,
    verifyShort: verify,
    credentialAndVerify: `${credential}${SEPARATOR}${verify}`,
  }
}

export function certificateLinesFromSnapshot(
  snapshotJson: string | null | undefined
): CertificateLines | null {
  if (!snapshotJson) {
    return null
  }
  try {
    const parsed = JSON.parse(snapshotJson) as {lines?: unknown}
    return parsed.lines && typeof parsed.lines === 'object'
      ? (parsed.lines as CertificateLines)
      : null
  } catch {
    return null
  }
}
